import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  algoliaSearch,
  AlgoliaSearchParams,
} from "../../../api/postCall/algoliaSearch";
import { AlgoliaSearchResponse } from "../../../data-model/algoliaSearchModel";

interface ExternalTablePoweredByAlgoliaState {
  data?: AlgoliaSearchResponse;
  loading?: boolean;
  error?: string;
  showinLineLoader?: boolean;
}

const initialState: ExternalTablePoweredByAlgoliaState = {
  data: undefined,
  loading: false,
  error: undefined,
  showinLineLoader: false,
};

const externalTablePoweredByAlgoliaSlice = createSlice({
  name: "externalTablePoweredByAlgolia",
  initialState,
  reducers: {
    setExternalTablePoweredByAlgoliaData: (
      state,
      action: PayloadAction<AlgoliaSearchResponse | undefined>,
    ) => {
      state.data = action.payload;
      state.loading = false;
      state.error = undefined;
      state.showinLineLoader = false;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      state.data = undefined;
      state.error = undefined;
      state.showinLineLoader = false;
    },
    setError: (state, action: PayloadAction<string | undefined>) => {
      state.error = action.payload;
      state.loading = false;
      state.data = undefined;
      state.showinLineLoader = false;
    },
    resetExternalTablePoweredByAlgoliaData: (state) => {
      state.data = undefined;
      state.loading = false;
      state.error = undefined;
      state.showinLineLoader = false;
    },
    setinLineLoader: (state, action: PayloadAction<boolean>) => {
      state.showinLineLoader = action.payload;
    },
    prependData: (state, action: PayloadAction<AlgoliaSearchResponse>) => {
      state.data = state.data
        ? {
            ...action.payload,
            hits: [
              ...(action.payload.hits ?? []),
              ...(state?.data?.hits ?? []),
            ],
          }
        : action.payload;
      state.loading = false;
      state.error = undefined;
      state.showinLineLoader = false;
    },
    appendData: (state, action: PayloadAction<AlgoliaSearchResponse>) => {
      state.data = state.data
        ? {
            ...action.payload,
            hits: [
              ...(state?.data?.hits ?? []),
              ...(action.payload.hits ?? []),
            ],
          }
        : action.payload;
      state.error = undefined;
      state.showinLineLoader = false;
    },
    setLoadingFalse: (state) => {
      state.loading = false;
    },
  },
});

export const {
  setExternalTablePoweredByAlgoliaData,
  setLoading,
  setError,
  resetExternalTablePoweredByAlgoliaData,
  setinLineLoader,
  prependData,
  appendData,
  setLoadingFalse,
} = externalTablePoweredByAlgoliaSlice.actions;

export const fetchExternalTablePoweredByAlgoliaData =
  (payload: AlgoliaSearchParams) => async (dispatch, getState: any) => {
    const { externalTablePoweredByAlgolia } = getState();
    if (externalTablePoweredByAlgolia.loading) {
      return;
    }
    dispatch(setLoading(true));
    try {
      const response = await algoliaSearch(payload);
      dispatch(
        setExternalTablePoweredByAlgoliaData(
          JSON.parse(response) as AlgoliaSearchResponse,
        ),
      );
    } catch (error) {
      dispatch(setError(error as string));
    }
  };

export const fetchMoreExternalTablePoweredByAlgoliaData =
  (payload: AlgoliaSearchParams) => async (dispatch, getState: any) => {
    const { externalTablePoweredByAlgolia } = getState();
    if (externalTablePoweredByAlgolia.loading) {
      return;
    }
    dispatch(setinLineLoader(true));
    try {
      const response = await algoliaSearch(payload);
      dispatch(appendData(JSON.parse(response) as AlgoliaSearchResponse));
    } catch (error) {
      dispatch(setError(error as string));
    }
  };

export const fetchPreviousExternalTablePoweredByAlgoliaData =
  (payload: AlgoliaSearchParams) => async (dispatch, getState: any) => {
    const { externalTablePoweredByAlgolia } = getState();
    if (externalTablePoweredByAlgolia.loading) {
      return;
    }
    dispatch(setinLineLoader(true));
    try {
      const response = await algoliaSearch(payload);
      dispatch(prependData(JSON.parse(response) as AlgoliaSearchResponse));
    } catch (error) {
      dispatch(setError(error as string));
    }
  };

export const fetchDataUptoGivenPageNumberFromExternalTablePoweredByAlgolia =
  (payloads: AlgoliaSearchParams[]) => async (dispatch, getState: any) => {
    const { externalTablePoweredByAlgolia } = getState();
    if (externalTablePoweredByAlgolia.loading) {
      return;
    }
    dispatch(setLoading(true));
    try {
      for (const params of payloads) {
        const response = await algoliaSearch(params);
        const parsed = JSON.parse(response) as AlgoliaSearchResponse;
        dispatch(appendData(parsed));
      }
    } catch (error) {
      dispatch(setError(error as string));
    } finally {
      dispatch(setLoadingFalse());
    }
  };

export default externalTablePoweredByAlgoliaSlice.reducer;
